import React from 'react';
import { Modal, Button, Tab, Nav, Card, Row, Col, Table, Alert, Badge } from 'react-bootstrap';
import PreventiviResultsTable from './PreventiviResultsTable';

const ServiceDetailsModal = ({ show, onHide, type, data }) => {
    if (!data) return null;

    const renderCensimentiDetails = () => {
        const request = data.request || {};
        const response = data.response || {};
        // In Censimenti.js, censimentiData is the response from the API.
        // Assuming data.response stores that exact structure.
        const censimentiData = response; 

        return (
            <Tab.Container defaultActiveKey="response">
                <Nav variant="pills" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="request">Richiesta</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="response">Risultato</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="request">
                        <Card>
                            <Card.Body>
                                <h6>Dati Inviati</h6>
                                <p><strong>Partita IVA / Codice Fiscale:</strong> {request.codicefiscale || request.partita_iva || '-'}</p>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="response">
                         {censimentiData && (
                            <>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">Informazioni Aziendali</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <p><strong>Ragione Sociale:</strong> {censimentiData.informazione_aziendale?.ragione_sociale || '-'}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Partita Iva:</strong> {censimentiData.informazione_aziendale?.codice_fiscale || '-'}</p>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {(() => {
                                    const censimentiEntries = Object.entries(censimentiData.censimenti || {}).filter(([, arr]) => Array.isArray(arr));
                                    const hasAnyResults = censimentiEntries.some(([, arr]) => arr.length > 0);

                                    if (!hasAnyResults) {
                                        return <Alert variant="info">Nessun censimento trovato o dati non disponibili.</Alert>;
                                    }

                                    return censimentiEntries.map(([companyName, results]) => {
                                        if (!results || results.length === 0) return null;

                                        return (
                                            <Card key={companyName} className="mb-3">
                                                <Card.Header>
                                                    <h6 className="mb-0">Risultati Censimenti - {companyName} ({results.length})</h6>
                                                </Card.Header>
                                                <Card.Body className="p-0">
                                                    <Table responsive bordered hover className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Provider</th>
                                                                <th>Tipo</th>
                                                                <th>Indice</th>
                                                                <th>Note</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {results.map((item, index) => {
                                                                const providerKey = Object.keys(item).find((key) => !['time', 'success'].includes(key));
                                                                const providerData = providerKey ? item[providerKey] : null;

                                                                if (!providerKey || !providerData) {
                                                                    return (
                                                                        <tr key={`${companyName}-${index}`}>
                                                                            <td colSpan={4}>Dato non valido</td>
                                                                        </tr>
                                                                    );
                                                                }

                                                                let indiceDisplay = providerData.indice_text ?? providerData.indice ?? 'N/A';
                                                                if (providerData.upgrade && providerData.upgrade > 0) {
                                                                    const baseIndice = providerData.indice || 0;
                                                                    const finalIndice = baseIndice + providerData.upgrade;
                                                                    indiceDisplay = `${baseIndice} + ${providerData.upgrade} = ${finalIndice}`;
                                                                }

                                                                const rowClass = providerData.assumibile ? 'table-success' : '';

                                                                return (
                                                                    <tr key={`${companyName}-${index}`} className={rowClass}>
                                                                        <td>{providerKey}</td>
                                                                        <td>{providerData.tipo_atc || '-'}</td>
                                                                        <td>{indiceDisplay}</td>
                                                                        <td>{providerData.nota || '-'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </Card.Body>
                                            </Card>
                                        );
                                    });
                                })()}
                            </>
                         )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    const renderCriteriDetails = () => {
        const request = data.request || {};
        const response = data.response || {};
        const prestiti = request.prestiti || [];

        // Helper to format key names
        const formatKey = (key) => {
            return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        };

        return (
            <Tab.Container defaultActiveKey="response">
                <Nav variant="pills" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="request">Richiesta</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="response">Risultato</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="request">
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Dati Richiesta</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {Object.entries(request).map(([key, value]) => {
                                        if (key === 'prestiti') return null;
                                        return (
                                            <Col md={6} key={key} className="mb-2">
                                                <strong>{formatKey(key)}:</strong> <span className="text-muted">{value || '-'}</span>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card.Body>
                        </Card>

                        {prestiti.length > 0 && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Impegni in Corso ({prestiti.length})</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive bordered hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Tipo</th>
                                                <th>Rata</th>
                                                <th>Durata</th>
                                                <th>Da Estinguere</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {prestiti.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td>{p.tipo}</td>
                                                    <td>{p.rata}</td>
                                                    <td>{p.durata}</td>
                                                    <td>{p.da_estinguere === 'on' ? 'Sì' : 'No'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="response">
                        {response.rejections && response.rejections.length > 0 && (
                            <Alert variant="danger">
                                <h6>Motivi di Scarto:</h6>
                                <ul className="mb-0">
                                    {response.rejections.map((r, idx) => (
                                        <li key={idx}>{r}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {response.possibilities && response.possibilities.length > 0 ? (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Possibilità ({response.possibilities.length})</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive bordered hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Banca</th>
                                                <th>Assicurazione</th>
                                                <th>Durata</th>
                                                <th>Rata Massima</th>
                                                <th>Montante</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {response.possibilities.map((possibility, index) => (
                                                <tr key={index}>
                                                    <td>{possibility[0]}</td>
                                                    <td>{possibility[1]}</td>
                                                    <td>{possibility[2]}</td>
                                                    <td>{possibility[3]}</td>
                                                    <td>{possibility[4]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        ) : (
                            !response.rejections && <Alert variant="warning">Nessuna possibilità trovata.</Alert>
                        )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    const renderPreventiviDetails = () => {
        const request = data.request || {};
        const response = data.response || {};
        
        // Helper to format key names
        const formatKey = (key) => {
            return key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        };

        return (
            <Tab.Container defaultActiveKey="response">
                <Nav variant="pills" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="request">Richiesta</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="response">Risultato</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="request">
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Dati Richiesta</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {Object.entries(request).map(([key, value]) => {
                                        if (typeof value === 'object') return null; // Skip complex objects like matched_combinations for now
                                        return (
                                            <Col md={6} key={key} className="mb-2">
                                                <strong>{formatKey(key)}:</strong> <span className="text-muted">{value !== null && value !== undefined ? String(value) : '-'}</span>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="response">
                        {Object.keys(response).length > 0 ? (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Risultati Preventivi</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <PreventiviResultsTable
                                        results={response}
                                        percentualeProvvigione={request.percentualeProvvigione || 0}
                                    />
                                </Card.Body>
                            </Card>
                        ) : (
                            <Alert variant="info">Nessun risultato disponibile.</Alert>
                        )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    const renderQuotaDetails = () => {
        const response = data.response || {};
        const request = data.request || {};
        const quotaData = response["Inps/Inpdap"];

        if (!quotaData) return <Alert variant="warning">Nessun dato disponibile per questa quota.</Alert>;

        return (
            <Tab.Container defaultActiveKey="response">
                <Nav variant="pills" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="request">Richiesta</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="response">Risultato</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="request">
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Dati Inviati</h6>
                            </Card.Header>
                            <Card.Body>
                                <p><strong>Codice Fiscale:</strong> {request.codicefiscale || '-'}</p>
                                <p><strong>Chiave Pensione:</strong> {request.chiavepensione || '-'}</p>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="response">
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Risultato Quota</h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table striped bordered hover size="sm" responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Istituto</th>
                                            <th>Chiave Pensione</th>
                                            <th>Codice Fiscale</th>
                                            <th>Esito</th>
                                            <th>Importo</th>
                                            <th>Nota</th>
                                            <th>Documento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>INPS/INPDAP</td>
                                            <td>{quotaData.chiave_pensione || '-'}</td>
                                            <td>{request.codicefiscale || '-'}</td>
                                            <td className="text-center">
                                                {quotaData.esito === 'OK' ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="green" viewBox="0 0 16 16">
                                                        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="red" viewBox="0 0 16 16">
                                                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                                                    </svg>
                                                )}
                                            </td>
                                            <td>{quotaData.importo || '-'}</td>
                                            <td>-</td>
                                            <td>
                                                {quotaData.pdfDocumento && (
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = 'data:application/pdf;base64,' + quotaData.pdfDocumento;
                                                            link.download = 'quota_cedibile.pdf';
                                                            link.click();
                                                        }}
                                                    >
                                                        Scarica PDF
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    const renderPrivacyDetails = () => {
        const privacy = data || {};

        const statusVariant = {
            CREATED: 'secondary',
            SENT: 'info',
            SIGNED: 'primary',
            CONFIRMED: 'success',
        }[privacy.status] || 'secondary';

        const formatDate = (dt) => (dt ? `${new Date(dt).toLocaleDateString('en-GB').replace(/\//g, '-')} ${new Date(dt).toLocaleTimeString('it-IT', {hour: '2-digit', minute:'2-digit'})}` : '-');
        const formatJustDate = (dt) => (dt ? new Date(dt).toLocaleDateString('en-GB').replace(/\//g, '-') : '-');

        return (
            <Card>
                <Card.Header>
                    <h6 className="mb-0">Dettagli Privacy</h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-2">
                            <strong>ID:</strong> <span className="text-muted">{privacy.id || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Stato:</strong>{' '}
                            <Badge bg={statusVariant}>{privacy.status || '-'}</Badge>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Contatto:</strong> <span className="text-muted">{privacy.contact_name || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Telefono:</strong> <span className="text-muted">{privacy.contact_phone || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Agente:</strong> <span className="text-muted">{privacy.agent_name || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>IP Address:</strong> <span className="text-muted">{privacy.ip_address || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Firmata il:</strong>{' '}
                            <span className="text-muted">{formatDate(privacy.signed_on)}</span>
                        </Col>
                        <Col md={12} className="mb-2">
                            <strong>User Agent:</strong>{' '}
                            <span className="text-muted" style={{ wordBreak: 'break-word' }}>{privacy.user_agent || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Privacy Check 1:</strong>{' '}
                            <span className="text-muted">{privacy.privacy_check1 ? 'Sì' : 'No'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Data Appuntamento:</strong>{' '}
                            <span className="text-muted">{formatJustDate(privacy.appointment_date)}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Ora Appuntamento:</strong>{' '}
                            <span className="text-muted">{privacy.appointment_time || '-'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Attiva:</strong>{' '}
                            <span className="text-muted">{privacy.is_active ? 'Sì' : 'No'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Scaduta:</strong>{' '}
                            <span className="text-muted">{privacy.is_expired ? 'Sì' : 'No'}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Creata il:</strong> <span className="text-muted">{formatDate(privacy.created_at)}</span>
                        </Col>
                        <Col md={6} className="mb-2">
                            <strong>Aggiornata il:</strong> <span className="text-muted">{formatDate(privacy.updated_at)}</span>
                        </Col>
                        <Col md={12} className="mb-2">
                            <strong>UUID:</strong> <span className="text-muted">{privacy.uuid || '-'}</span>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderInviaAffidaDetails = () => {
        const request = data.request || {};
        const response = data.response || {};

        const formatDate = (dt) => (dt ? `${new Date(dt).toLocaleDateString('en-GB').replace(/\//g, '-')} ${new Date(dt).toLocaleTimeString('it-IT', {hour: '2-digit', minute:'2-digit'})}` : '-');

        const fieldLabels = {
            campagnaID: 'Campagna ID',
            nome: 'Nome',
            cognome: 'Cognome',
            email: 'Email',
            cellulare: 'Cellulare',
            note: 'Note (Residenza)',
            'c__xcNDpweA': 'Importo Richiesto',
            'c__tqybsn4x': 'Professione',
            'c__gocyy64v': 'Data di Nascita',
            'c__privacy1': 'Privacy Check 1',
            'c__BYBIDFRZ': 'Tipologia Lead',
            'c__hyez2gj1': 'Canale Lead',
            'c__fowc2kq5': 'Fornitore Lead',
            'c__leadidperf': 'ID Lead Fornitore',
            'c__h5q9jg2s': 'Prodotto Finanziario',
            'c__20ub2s8y': 'Sesso',
            'c__mjslq774': 'Data Richiesta',
            'c__YIekwdwT': 'Indirizzo IP',
            'c__eiUtEtZk': 'Timestamp Generazione',
            'c__LEguCSus': 'User Agent',
        };

        return (
            <Tab.Container defaultActiveKey="response">
                <Nav variant="pills" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="request">Richiesta</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="response">Risultato</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="request">
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Dati Inviati</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {Object.entries(request).map(([key, value]) => (
                                        <Col md={6} key={key} className="mb-2">
                                            <strong>{fieldLabels[key] || key}:</strong>{' '}
                                            <span className="text-muted" style={{ wordBreak: 'break-word' }}>
                                                {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
                                            </span>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                        {data.request_url && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">URL Richiesta</h6>
                                </Card.Header>
                                <Card.Body>
                                    <code style={{ wordBreak: 'break-all' }}>{data.request_url}</code>
                                </Card.Body>
                            </Card>
                        )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="response">
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Esito Invio</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4} className="mb-2">
                                        <strong>Stato:</strong>{' '}
                                        <Badge bg={data.success ? 'success' : 'danger'}>
                                            {data.success ? 'Successo' : 'Errore'}
                                        </Badge>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <strong>HTTP Status:</strong>{' '}
                                        <span className="text-muted">{data.status_code ?? '-'}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <strong>Inviato il:</strong>{' '}
                                        <span className="text-muted">{formatDate(data.created_at)}</span>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Risposta</h6>
                            </Card.Header>
                            <Card.Body>
                                {response && Object.keys(response).length > 0 ? (
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {JSON.stringify(response, null, 2)}
                                    </pre>
                                ) : (
                                    <Alert variant="info" className="mb-0">Nessuna risposta disponibile.</Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        );
    };

    const renderContent = () => {
        switch(type) {
            case 'Censimenti':
                return renderCensimentiDetails();
            case 'Quota':
                return renderQuotaDetails();
            case 'Criteri':
                return renderCriteriDetails();
            case 'Preventivi':
                return renderPreventiviDetails();
            case 'Privacy':
                return renderPrivacyDetails();
            case 'Invia Affida':
                return renderInviaAffidaDetails();
            default:
                return <p>Seleziona un servizio per vedere i dettagli.</p>;
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Dettagli {type}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderContent()}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Chiudi</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ServiceDetailsModal;
